import { apiConfig } from '@/config/api';
import { authenticatedFetch, APIError } from '@/lib/api/authenticatedFetch';

// ============================================================================
// SCHEDULE TYPES
// ============================================================================

export interface VINProcessingConfig {
  oem?: string;
  sampling_strategy?: 'all' | 'random' | 'first' | 'last';
  sampling_count?: number;
  entry_point?: 'step1_execution';
}

export interface Schedule {
  id: string;
  schedule_name: string;
  apify_schedule_id: string;
  task_name: string;
  tenant_id: string;
  cron_expression: string;
  time_zone: string;
  is_enabled: boolean;
  input_payload: Record<string, unknown>;
  auto_trigger_vin_processing: boolean;
  vin_processing_config: VINProcessingConfig;
  description: string | null;
  last_triggered_at: string | null;
  last_run_id: string | null;
  last_run_status: string | null;
  run_count: number;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
  tenants?: {
    tenant_slug: string;
    tenant_name: string;
  };
}

export interface ScheduleCreateRequest {
  task_name: string;
  tenant_id: string;
  cron_expression: string;
  time_zone?: string;
  description?: string;
  input_payload?: Record<string, unknown>;
  auto_trigger_vin_processing?: boolean;
  vin_processing_config?: VINProcessingConfig;
  is_enabled?: boolean;
}

export interface ScheduleUpdateRequest {
  cron_expression?: string;
  time_zone?: string;
  description?: string;
  input_payload?: Record<string, unknown>;
  auto_trigger_vin_processing?: boolean;
  vin_processing_config?: VINProcessingConfig;
  is_enabled?: boolean;
}

export interface ScheduleListResponse {
  schedules: Schedule[];
  count: number;
}

export interface ScheduleListFilters {
  tenant_id?: string;
  task_name?: string;
  is_enabled?: boolean;
  limit?: number;
  offset?: number;
}

export interface TriggerScheduleResponse {
  schedule_id: string;
  run_id: string;
  status: string;
  message: string;
}

// ============================================================================
// RUNNING STATUS TYPES
// ============================================================================

export interface CurrentRun {
  id: string;
  apify_run_id: string;
  started_at: string;
}

export interface ScheduleWithRunningStatus extends Schedule {
  is_running: boolean;
  current_run: CurrentRun | null;
}

export interface RunningSchedulesResponse {
  status: string;
  schedules: ScheduleWithRunningStatus[];
  count: number;
  running_count: number;
  timestamp: string;
}

export interface RunningExecution {
  id: string;
  task_name: string;
  tenant_id: string;
  apify_run_id: string;
  status: string;
  started_at: string;
  trigger_mode: string;
  oem?: string;
  schedule?: {
    id: string;
    schedule_name: string;
  };
}

export interface RunningExecutionsResponse {
  status: string;
  running_count: number;
  executions: RunningExecution[];
  timestamp: string;
}

// ============================================================================
// AUTOMATION RULE TYPES
// ============================================================================

export interface TriggerConditions {
  source?: 'data_collection';
  status?: 'completed';
  task_name?: string;
  oem?: string;
  min_records?: number;
}

export interface ActionConfig {
  oem?: string;
  sampling_strategy?: 'all' | 'random' | 'first' | 'last';
  sampling_count?: number;
  entry_point?: 'step1_execution';
}

export interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  rule_type: 'data_collection_to_vin_processing';
  tenant_id: string | null;
  trigger_conditions: TriggerConditions;
  action_type: 'trigger_vin_processing';
  action_config: ActionConfig;
  is_enabled: boolean;
  priority: number;
  last_executed_at: string | null;
  execution_count: number;
  created_at: string;
  created_by: string;
  updated_at: string;
  tenants?: {
    tenant_slug: string;
    tenant_name: string;
  } | null;
}

export interface RuleCreateRequest {
  name: string;
  description?: string;
  tenant_id?: string | null;
  trigger_conditions: TriggerConditions;
  action_config?: ActionConfig;
  is_enabled?: boolean;
  priority?: number;
}

export interface RuleUpdateRequest {
  name?: string;
  description?: string;
  trigger_conditions?: TriggerConditions;
  action_config?: ActionConfig;
  is_enabled?: boolean;
  priority?: number;
}

export interface RuleListResponse {
  rules: AutomationRule[];
  count: number;
}

export interface RuleListFilters {
  tenant_id?: string;
  is_enabled?: boolean;
  limit?: number;
  offset?: number;
}

// ============================================================================
// EXECUTION TYPES
// ============================================================================

export interface RuleExecution {
  id: string;
  rule_id: string;
  schedule_id: string | null;
  source_run_id: string;
  source_apify_run_id: string;
  source_task_name: string;
  source_tenant_id: string;
  action_type: string;
  status: 'pending' | 'success' | 'failed' | 'skipped';
  error_message: string | null;
  vin_job_id: string | null;
  triggered_by: 'automation' | 'manual' | 'schedule';
  triggered_by_user_id: string | null;
  executed_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  automation_rules?: {
    name: string;
  };
}

export interface ExecutionListResponse {
  executions: RuleExecution[];
  count: number;
}

export interface ExecutionListFilters {
  status?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// CRON PRESETS
// ============================================================================

export interface CronPreset {
  label: string;
  cron: string;
  description: string;
}

export const CRON_PRESETS: CronPreset[] = [
  { label: 'Every day at midnight', cron: '0 0 * * *', description: 'Runs daily at 12:00 AM' },
  { label: 'Every day at 6 AM', cron: '0 6 * * *', description: 'Runs daily at 6:00 AM' },
  { label: 'Every Monday at 6 AM', cron: '0 6 * * 1', description: 'Runs weekly on Mondays' },
  {
    label: 'Every weekday at 6 AM',
    cron: '0 6 * * 1-5',
    description: 'Runs Monday through Friday'
  },
  { label: 'Every 6 hours', cron: '0 */6 * * *', description: 'Runs 4 times per day' },
  { label: 'Every 12 hours', cron: '0 */12 * * *', description: 'Runs twice per day' },
  { label: 'First day of month', cron: '0 0 1 * *', description: 'Runs monthly at midnight' }
];

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' }
];

// ============================================================================
// SCHEDULING API CLASS
// ============================================================================

class SchedulingAPI {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Scheduling endpoints are on the data collection service
    this.baseUrl = apiConfig.dataCollection.baseUrl;
    this.timeout = apiConfig.dataCollection.timeout;
  }

  // ========================================================================
  // SCHEDULE ENDPOINTS
  // ========================================================================

  /**
   * List all schedules with optional filters
   */
  async listSchedules(filters?: ScheduleListFilters): Promise<ScheduleListResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const params = new URLSearchParams();
    if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
    if (filters?.task_name) params.set('task_name', filters.task_name);
    if (filters?.is_enabled !== undefined) params.set('is_enabled', String(filters.is_enabled));
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `${this.baseUrl}/schedules${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Get a specific schedule by ID
   */
  async getSchedule(id: string): Promise<Schedule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Create a new schedule
   */
  async createSchedule(data: ScheduleCreateRequest): Promise<Schedule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/schedules`, {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Update an existing schedule
   */
  async updateSchedule(id: string, data: ScheduleUpdateRequest): Promise<Schedule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Delete a schedule
   */
  async deleteSchedule(id: string): Promise<void> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    await authenticatedFetch(`${this.baseUrl}/schedules/${id}`, {
      method: 'DELETE',
      timeout: this.timeout
    });
  }

  /**
   * Manually trigger a schedule
   */
  async triggerSchedule(id: string): Promise<TriggerScheduleResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/schedules/${id}/trigger`, {
      method: 'POST',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Get schedules with running status
   * Returns all schedules with is_running flag and current_run info
   */
  async getRunningSchedules(tenantId?: string): Promise<RunningSchedulesResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const params = new URLSearchParams();
    if (tenantId) params.set('tenant_id', tenantId);

    const queryString = params.toString();
    const url = `${this.baseUrl}/schedules/running${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Get currently running executions
   */
  async getRunningExecutions(tenantId?: string): Promise<RunningExecutionsResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const params = new URLSearchParams();
    if (tenantId) params.set('tenant_id', tenantId);

    const queryString = params.toString();
    const url = `${this.baseUrl}/executions/running${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  // ========================================================================
  // AUTOMATION RULE ENDPOINTS
  // ========================================================================

  /**
   * List all automation rules with optional filters
   */
  async listRules(filters?: RuleListFilters): Promise<RuleListResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const params = new URLSearchParams();
    if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
    if (filters?.is_enabled !== undefined) params.set('is_enabled', String(filters.is_enabled));
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `${this.baseUrl}/automation-rules${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Get a specific automation rule by ID
   */
  async getRule(id: string): Promise<AutomationRule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/automation-rules/${id}`, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Create a new automation rule
   */
  async createRule(data: RuleCreateRequest): Promise<AutomationRule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/automation-rules`, {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Update an existing automation rule
   */
  async updateRule(id: string, data: RuleUpdateRequest): Promise<AutomationRule> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/automation-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Delete an automation rule
   */
  async deleteRule(id: string): Promise<void> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    await authenticatedFetch(`${this.baseUrl}/automation-rules/${id}`, {
      method: 'DELETE',
      timeout: this.timeout
    });
  }

  /**
   * Get execution history for a specific rule
   */
  async getRuleExecutions(
    ruleId: string,
    filters?: ExecutionListFilters
  ): Promise<ExecutionListResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `${this.baseUrl}/automation-rules/${ruleId}/executions${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }
}

export const schedulingAPI = new SchedulingAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert a cron expression to human-readable text
 */
export function describeCron(cron: string): string {
  if (!cron) return 'Not set';

  // Check against presets first
  const preset = CRON_PRESETS.find(p => p.cron === cron);
  if (preset) return preset.label;

  // Parse the cron expression
  const parts = cron.split(' ');
  if (parts.length !== 5) return `Custom: ${cron}`;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Build description based on parts
  const descriptions: string[] = [];

  // Time
  if (minute !== '*' && hour !== '*') {
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
    descriptions.push(`at ${displayHour}:${minuteNum.toString().padStart(2, '0')} ${period}`);
  }

  // Day of week
  if (dayOfWeek !== '*') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    if (dayOfWeek.includes('-')) {
      const [start, end] = dayOfWeek.split('-').map(Number);
      descriptions.push(`${days[start]} to ${days[end]}`);
    } else if (dayOfWeek.includes(',')) {
      const dayNames = dayOfWeek.split(',').map(d => days[parseInt(d)]);
      descriptions.push(`on ${dayNames.join(', ')}`);
    } else {
      descriptions.push(`on ${days[parseInt(dayOfWeek)]}`);
    }
  }

  // Day of month
  if (dayOfMonth !== '*') {
    descriptions.push(`on day ${dayOfMonth}`);
  }

  // Month
  if (month !== '*') {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    descriptions.push(`in ${months[parseInt(month) - 1]}`);
  }

  return descriptions.length > 0 ? descriptions.join(' ') : `Custom: ${cron}`;
}

/**
 * Validate a cron expression
 */
export function validateCron(cron: string): { valid: boolean; error?: string } {
  if (!cron) {
    return { valid: false, error: 'Cron expression is required' };
  }

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return {
      valid: false,
      error: 'Cron expression must have 5 parts (minute hour day month weekday)'
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Validate minute (0-59)
  if (!isValidCronPart(minute, 0, 59)) {
    return { valid: false, error: 'Invalid minute value (0-59)' };
  }

  // Validate hour (0-23)
  if (!isValidCronPart(hour, 0, 23)) {
    return { valid: false, error: 'Invalid hour value (0-23)' };
  }

  // Validate day of month (1-31)
  if (!isValidCronPart(dayOfMonth, 1, 31)) {
    return { valid: false, error: 'Invalid day of month value (1-31)' };
  }

  // Validate month (1-12)
  if (!isValidCronPart(month, 1, 12)) {
    return { valid: false, error: 'Invalid month value (1-12)' };
  }

  // Validate day of week (0-7, where 0 and 7 are Sunday)
  if (!isValidCronPart(dayOfWeek, 0, 7)) {
    return { valid: false, error: 'Invalid day of week value (0-7)' };
  }

  return { valid: true };
}

function isValidCronPart(part: string, min: number, max: number): boolean {
  if (part === '*') return true;

  // Handle */n (step values)
  if (part.startsWith('*/')) {
    const step = parseInt(part.slice(2));
    return !isNaN(step) && step > 0;
  }

  // Handle ranges (e.g., 1-5)
  if (part.includes('-')) {
    const [start, end] = part.split('-').map(Number);
    return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
  }

  // Handle lists (e.g., 1,3,5)
  if (part.includes(',')) {
    return part.split(',').every(p => {
      const num = parseInt(p);
      return !isNaN(num) && num >= min && num <= max;
    });
  }

  // Single value
  const num = parseInt(part);
  return !isNaN(num) && num >= min && num <= max;
}
